import * as _ from 'lodash';
import {
    WboAnnotationData,
    WboAnnotationDocumentData, WboAnnotationDocumentMetadata, WboBatchedBroadcastMsg, WboChildPointData, WboDelta, WboMsg
} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import {
    addVersion,
    mergeVersionStrings,
    nowInMicroseconds
} from '@/components/common/ed-board/ed-board-tools/wbo-client/util/wbo-utils';

/**
 * Represents a Wbo annotations document in memory.
 */
export default class WboDocument {
    private documentData: WboAnnotationDocumentData;

    constructor(documentData?: WboAnnotationDocumentData) {
        if (documentData && typeof documentData !== 'object') {
            throw new Error('Invalid document data provided to WboDocument constructor');
        }
        
        this.documentData = documentData || {
            metadata: { versionId: 0, appliedVersions: '0' }
        } as WboAnnotationDocumentData;
    }


    setData(data: WboAnnotationDocumentData) {
        this.documentData = data;
    }

    getData(): WboAnnotationDocumentData {
        return this.documentData;
    }

    getVersion(): number {
        return this.documentData?.metadata?.versionId ?? 0;
    }

    /**
     * Sets the version of the document. Also applies that version to the applied versions list.
     */
    setVersion(version: number) {
        if (version === undefined) {
            return;
        }
        if (version > this.documentData?.metadata?.versionId) {
            _.set(this.documentData, 'metadata.versionId', version);
        }

        _.set(this.documentData, 'metadata.appliedVersions',
            addVersion(this.documentData?.metadata?.appliedVersions, version));
    }

    /**
     * Applies a version string to the document. This is used to keep track of the versions that have been applied to the
     * document. The version string is a comma-separated list of version numbers or ranges (e.g. "1,3-5,7").
     */
    applyVersionString(versionString: string) {
        _.set(this.documentData, 'metadata.appliedVersions',
            mergeVersionStrings(this.documentData?.metadata?.appliedVersions, versionString));
    }

    setOtherMetadata(metadata: WboAnnotationDocumentMetadata) {
        const { appliedVersions, versionId, ...otherMetadata } = metadata || {};
        Object.keys(otherMetadata).forEach(key => {
            this.documentData.metadata[key] = otherMetadata[key];
        });
    }

    /**
     * Applies an operation to the document data. Returns the operation that was applied, in a form that the WBO drawer
     * adapter can understand.
     */
    applyOperation(op: WboDelta): WboDelta {
        if (op.opType === 'broadcast') {
            const opMsg = op.msg as WboMsg;
            switch (opMsg.data.type) {
                case 'delete':
                    this.applyDeleteAnnotation(op);
                    break;
                case 'update':
                    this.applyUpdateAnnotation(op);
                    break;
                case 'child':
                    this.applyChildAnnotation(op);
                    break;
                case 'line':
                case 'straight':
                    this.applyAddAnnotation(op);
                    break;
                case 'finish':
                    break;
                default:
                    throw new Error(`Unsupported message type "${opMsg.data.type}" for WBO operation "${op.opType}"`);
            }
            // Return an efficient clone of the original op
            return this.deepClone(op);

        } else if (op.opType === 'removeAllByUser' || op.opType === 'removeForUpload') {
            const opMsg = (op.msg as WboMsg);
            const deletedAnnotations = this.applyRemoveAllByUser(op);
            return {
                ...op, msg: {
                    board: op.msg.board,
                    clientId: op.msg.clientId,
                    elementsToDelete: deletedAnnotations,
                    operationId: opMsg.operationId,
                    userName: op.msg.userName
                }
            };
        } else if (op.opType === 'removeAllForUpload') {
            const opMsg = op.msg as WboMsg;
            const deletedAnnotations = this.applyRemoveAll(op);
            return {
                ...op, msg: {
                    board: op.msg.board,
                    clientId: op.msg.clientId,
                    elementsToDelete: deletedAnnotations,
                    operationId: opMsg.operationId,
                    userName: op.msg.userName
                }
            };
        }
        else if (op.opType === 'broadcastMultiple') {
            const opMsg = op.msg as WboBatchedBroadcastMsg;
            // Recursively apply all operations in the batch
            for (const groupedMessage of opMsg.data) {
                this.applyOperation({
                    opType: 'broadcast', msg: {
                        board: op.msg.board,
                        clientId: op.msg.clientId,
                        operationId: groupedMessage.operationId ?? nowInMicroseconds(),
                        userName: op.msg.userName,
                        data: groupedMessage
                    }, v: op.v
                });
            }
        }
        else {
            throw new Error(`Unsupported WBO operation: "${op.opType}"`);
        }
    }

    /**
     * Resets the document to its initial state.
     */
    reset() {
        this.documentData = {metadata: {versionId: 0}} as WboAnnotationDocumentData;
    }

    private applyDeleteAnnotation(op: WboDelta) {
        this.setVersion(op.v);
        delete this.documentData[(op.msg as WboMsg).data.id];
    }

    private applyAddAnnotation(op: WboDelta) {
        this.setVersion(op.v);
        let id = (op.msg as WboMsg).data.id;
        this.documentData[id] = _.omit(this.deepClone(op.msg.data), ['socket', 'boardName']);
        // Optimize children array
        if (Array.isArray(this.documentData[id]._children)) {
            // @ts-ignore
            this.documentData[id]._children = this.documentData[id]._children
                .map(child => this.cloneChildPointData(child as WboChildPointData));
        }
    }

    private applyUpdateAnnotation(op: WboDelta) {
        this.setVersion(op.v);
        const annotation: WboAnnotationData = this.documentData[(op.msg as WboMsg).data.id];
        if (annotation) {
            _.merge(annotation, _.pick(op.msg.data, ['deltax', 'deltay', 'x2', 'y2'])); // Only update updatable fields
        }
    }

    private applyChildAnnotation(op: WboDelta): boolean {
        let parentId = (op?.msg?.data as WboChildPointData).parent;
        if (!parentId) {
            return false;
        }
        const parent = this.documentData[parentId];
        if (!parent) {
            return false;
        }
        this.setVersion(op.v);
        let child = this.cloneChildPointData(op.msg.data as WboChildPointData);
        if (Array.isArray(parent._children)) {
            let previousChild = parent._children[parent._children.length - 1] as WboChildPointData;
            if (previousChild?.x == child?.x && previousChild?.y == child?.y) {
                return true;
            }
            parent._children.push(child);
        } else {
            parent._children = [child];
        }
        return true;
    }

    /**
     * Removes all user board annotations in the board, with the following rules:
     * If the message contains isHost = true, all annotations are deleted.
     * Otherwise, if isOwner = true, only the annotation owned by the specified userName are deleted.
     * Otherwise, both anonymous and annotations owned by the specified userName are deleted.
     */
    private applyRemoveAllByUser(op: WboDelta): string[] {
        this.setVersion(op.v);
        let msg = op.msg as WboMsg;
        const elementsToDelete = msg.elementsToDelete || [];
        elementsToDelete.push(...this.getElementsToDelete(msg));
        elementsToDelete.forEach(key => delete this.documentData[key]);
        return elementsToDelete;
    }

    /**
     * Removes all annotations.
     */
    private applyRemoveAll(op: WboDelta): string[] {
        this.setVersion(op.v);
        const elementsToDelete = [];
        for (const key in this.documentData) {
            if (key === 'metadata') {
                continue;
            }
            elementsToDelete.push(key);
            delete this.documentData[key];
        }
        return elementsToDelete;

    }

    /**
     * Returns the elements to delete based on the removeAllByUser rules.
     */
    private getElementsToDelete(msg: WboMsg): string[] {
        if (!msg || (!msg.isHost && !msg.isOwner && !msg.userName)) {
            return [];
        }
        const elementsToDelete: string[] = [];
        for (const key in this.documentData) {
            if (key === 'metadata') {
                continue;
            }
            if (msg.isHost) {
                // Deletes all
                elementsToDelete.push(key);
            }
            else if (msg.isOwner) {
                if (this.documentData[key].userName === msg.userName || !this.documentData[key].userName) {
                    // Deletes own and anonymous annotations
                    elementsToDelete.push(key);
                }
            }
            else if (this.documentData[key].userName === msg.userName) {
                // Deletes own annotations only
                elementsToDelete.push(key);
            }
        }
        return elementsToDelete;
    }


    /**
     * Efficient deep cloning utility that uses structuredClone when available,
     * falls back to JSON serialization for compatibility.
     */
    private deepClone<T>(obj: T): T {
        if (typeof structuredClone !== 'undefined') {
            return structuredClone(obj);
        }
        // Fallback for older environments
        return JSON.parse(JSON.stringify(obj));
    }

    /**
     * Optimized cloning for child point data that only copies necessary fields.
     */
    private cloneChildPointData(data: WboChildPointData): WboChildPointData {
        return {
            parent: data.parent,
            tool: data.tool,
            type: data.type,
            x: data.x,
            y: data.y
        };
    }
}
